import React from "react";
import { ArrowRight } from "lucide-react";
import { Reveal, CreditChip } from "@/components/site/primitives";
import { INTENTS } from "@/content/worldFarmers";
import { Link } from "react-router-dom";

// Each intent card routes to its dedicated page.
const TARGET = { "where-to-buy": "/where-to-buy", support: "/support", "who-we-are": "/about" };
const OFFSET = ["lg:mt-0", "lg:mt-12", "lg:mt-4"]; // staggered, organic

const Card = ({ intent, offset }) => {
  return (
    <article
      id={intent.id}
      data-testid={`intent-card-${intent.id}`}
      className={`group flex transform-gpu flex-col overflow-hidden rounded-sm border border-[var(--wf-border)] bg-white shadow-[var(--wf-shadow-sm)] [isolation:isolate] [backface-visibility:hidden] transition-[box-shadow,transform] duration-300 hover:shadow-[var(--wf-shadow)] motion-safe:hover:-translate-y-1.5 ${offset}`}
    >
      <div className="relative overflow-hidden rounded-t-sm">
        <img src={intent.image} alt={intent.alt} loading="lazy" className="aspect-[4/3] w-full transform-gpu object-cover transition-transform duration-[800ms] ease-out group-hover:scale-105" />
        <CreditChip src={intent.image} className="pointer-events-none absolute left-3 top-3 rounded-full bg-white/85 px-2.5 py-0.5 text-[10px] font-medium text-[var(--wf-soil)] backdrop-blur-sm opacity-100 sm:opacity-0 sm:group-hover:opacity-100" />
        <span className="absolute right-3 top-3 rounded-full bg-[var(--wf-farm-green)] px-3 py-1 text-[11px] font-semibold text-white">{intent.kicker}</span>
      </div>
      <div className="flex flex-1 flex-col p-7">
        <h3 className="font-serif text-2xl font-semibold text-[var(--wf-ink)]">{intent.title}</h3>
        <p className="mt-2 flex-1 text-sm leading-6 text-muted-foreground">{intent.body}</p>
        <Link
          to={TARGET[intent.id] || "/"}
          data-testid={`intent-cta-${intent.id}`}
          className="mt-5 inline-flex w-fit items-center gap-2 text-sm font-semibold text-[var(--wf-maroon)] transition-colors hover:text-[var(--wf-maroon-dark)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background rounded-sm"
        >
          {intent.cta}
          <span className="grid h-7 w-7 place-items-center rounded-full bg-[var(--wf-wheat)] transition-colors group-hover:bg-[var(--wf-maroon)] group-hover:text-white">
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
        </Link>
      </div>
    </article>
  );
};

export const OfferingsV4 = ({ overlap = true }) => {
  const ordered = [
    INTENTS.find((i) => i.id === "who-we-are"),
    INTENTS.find((i) => i.id === "support"),
    INTENTS.find((i) => i.id === "where-to-buy"),
  ].filter(Boolean);
  return (
    <section aria-labelledby="offerings-v4-heading" className={`relative z-10 bg-white ${overlap ? "-mt-8 rounded-t-sm pt-16 sm:pt-20 lg:-mt-12 lg:pt-24" : "pt-16 sm:pt-20 lg:pt-28"}`}>
      <div className="wf-container pb-16 sm:pb-20 lg:pb-24">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--wf-green-text)]">
            Explore World Farmers
          </span>
          <h2 id="offerings-v4-heading" className="mt-3 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
            Find your way to dig in
          </h2>
          <p className="mt-3 text-base leading-7 text-muted-foreground">
            Shop the harvest, lend your support, or get to know the community growing it.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {ordered.map((intent, i) => (
            <Reveal key={intent.id} delay={0.08 * i}>
              <Card intent={intent} offset={OFFSET[i]} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
